      
      
import {
  GoogleMap,
  InfoWindowF,
  Marker,
  MarkerF,
  OverlayViewF,
  Polygon,
  PolygonF,
  useJsApiLoader,
} from '@react-google-maps/api'
import { filterLatLong } from 'helpers'
import React, { useEffect, useMemo, useState, useRef } from 'react'
import { createHeatmap } from './heatmap'

export { ACTIVITY_COLORS, DENSITY_COLORS, RISK_COLORS } from './heatmap'

const MapMarker = MarkerF || Marker
const MapPolygon = PolygonF || Polygon

const NORMAL_COLOR = '#006386'
const NORMAL_FILL = '#7ECBE5'
const SELECTED_COLOR = '#D92D20'
const SHAPE_PATHS = {
  square: 'M -6 -6 L 6 -6 L 6 6 L -6 6 Z',
  diamond: 'M 0 -8 L 8 0 L 0 8 L -8 0 Z',
}

const OPTIONS = {
  minZoom: 4,
  maxZoom: 20,
  mapTypeId: 'satellite',
}

const exploreOptions = (mapTypeId) => ({
  mapTypeId,
  isFractionalZoomEnabled: true,
  minZoom: OPTIONS.minZoom,
  maxZoom: OPTIONS.maxZoom,
  gestureHandling: 'greedy',
  clickableIcons: false,
  mapTypeControl: false,
  fullscreenControl: false,
  zoomControl: window.innerWidth > 768,
  streetViewControl: false,
  zoomControlOptions: {
    position: window.google.maps.ControlPosition.RIGHT_BOTTOM,
  },
})

const insidePolygon = ([x, y], ring) => {
  let inside = false
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [xi, yi] = ring[i]
    const [xj, yj] = ring[j]
    if (yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) {
      inside = !inside
    }
  }
  return inside
}

export const polygonCenter = (points) => {
  const ring = (points || [])
    .map((p) => p?.plottingPoint?.coordinates)
    .filter((c) => c?.length === 2)
  if (ring.length < 3) return null
  let area = 0
  let cx = 0
  let cy = 0
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const f = ring[j][0] * ring[i][1] - ring[i][0] * ring[j][1]
    area += f
    cx += (ring[j][0] + ring[i][0]) * f
    cy += (ring[j][1] + ring[i][1]) * f
  }
  if (!area) return null
  const center = [cx / (3 * area), cy / (3 * area)]
  return insidePolygon(center, ring) ? { lat: center[1], lng: center[0] } : null
}

export const getLatLng = (el) => {
  const center = polygonCenter(el?.plottingPoints)
  if (center) return center
  const c = el?.locationPoint?.coordinates ?? el?.plottingPoint?.coordinates
  return c?.length ? { lat: c[1], lng: c[0] } : null
}

const getItemBounds = (items) => {
  const bounds = new window.google.maps.LatLngBounds()
  items.forEach((el) => {
    filterLatLong(el?.plottingPoints || []).forEach((c) => bounds.extend(c))
    const p = getLatLng(el)
    if (p) bounds.extend(p)
  })
  return bounds
}

const zoomForBounds = (map, bounds, pad) => {
  const div = map.getDiv()
  const ne = bounds.getNorthEast()
  const sw = bounds.getSouthWest()
  const latRad = (lat) => {
    const s = Math.sin((lat * Math.PI) / 180)
    return Math.log((1 + s) / (1 - s)) / 2
  }
  const latFrac = (latRad(ne.lat()) - latRad(sw.lat())) / Math.PI
  const lngFrac = ((ne.lng() - sw.lng() + 360) % 360) / 360
  const fit = (px, frac) => Math.log2(px / 256 / frac)
  const w = div.clientWidth - (pad.left || 0) - (pad.right || 0)
  const h = div.clientHeight - (pad.top || 0) - (pad.bottom || 0)
  return Math.max(4, Math.floor(Math.min(fit(w, lngFrac), fit(h, latFrac), 18)))
}

const flyTo = (map, bounds, pad, flightRef) => {
  const id = ++flightRef.current
  const { event } = window.google.maps
  const next = (fn) => {
    let done = false
    const l = event.addListenerOnce(map, 'idle', () => {
      done = true
      if (flightRef.current === id) fn()
    })
    setTimeout(() => {
      if (!done) {
        l.remove()
        if (flightRef.current === id) fn()
      }
    }, 700)
  }
  const center = bounds.getCenter()
  const target = zoomForBounds(map, bounds, pad)
  const zoomIn = () => {
    const z = map.getZoom()
    if (z >= target) return map.fitBounds(bounds, pad)
    map.setZoom(z + 1)
    next(zoomIn)
  }
  const travel = () => {
    if (!map.getBounds()?.contains(center) && map.getZoom() > 4) {
      map.setZoom(map.getZoom() - 1)
      return next(travel)
    }
    map.panTo(center)
    next(zoomIn)
  }
  travel()
}

const animateTo = (map, bounds, pad, flightRef) => {
  const id = ++flightRef.current
  const proj = map.getProjection()
  const z0 = map.getZoom()
  const z1 = zoomForBounds(map, bounds, pad)
  const scale = 2 ** z1
  const p1 = proj.fromLatLngToPoint(bounds.getCenter())
  p1.x -= ((pad.left || 0) - (pad.right || 0)) / 2 / scale
  p1.y -= ((pad.top || 0) - (pad.bottom || 0)) / 2 / scale
  const p0 = proj.fromLatLngToPoint(map.getCenter())
  const distPx = Math.hypot(p1.x - p0.x, p1.y - p0.y) * 2 ** Math.min(z0, z1)
  const bump = Math.max(0, Math.log2(distPx / map.getDiv().clientWidth) + 1)
  const duration = Math.min(1500, 600 + bump * 150)
  const ease = (t) => (t < 0.5 ? 4 * t * t * t : 1 - (-2 * t + 2) ** 3 / 2)
  const start = performance.now()
  const frame = (now) => {
    if (flightRef.current !== id) return
    const t = Math.min(1, (now - start) / duration)
    const e = ease(t)
    const point = new window.google.maps.Point(
      p0.x + (p1.x - p0.x) * e,
      p0.y + (p1.y - p0.y) * e,
    )
    map.moveCamera({
      center: proj.fromPointToLatLng(point),
      zoom: z0 + (z1 - z0) * e - bump * Math.sin(Math.PI * t),
    })
    if (t < 1) requestAnimationFrame(frame)
  }
  requestAnimationFrame(frame)
}

const containerStyle = {
  width: '100%',
  height: '100%',
}

export const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY

function Map({
  data,
  center,
  multiple = false,
  onSelectLand,
  dataMap,
  onShowDetailLand,
  selectedCodes = [],
  fitPadding = 60,
  layers = { points: true, parcels: true, labels: true },
  colorFor,
  overlays = [],
  heatmap,
  onZoomLevel,
  controls,
}) {
  const [map, setMap] = useState(null)
  const mapRef = useRef(null)
  const flightRef = useRef(0)
  const fittedRef = useRef('')
  const allKeyRef = useRef('')
  const [showLabels, setShowLabels] = useState(true)
  const [activeOverlay, setActiveOverlay] = useState(null)
  const [mapType, setMapType] = useState('satellite')

  const googleMapsApiKey = GOOGLE_MAPS_API_KEY
  const googleMapsId = process.env.REACT_APP_GOOGLE_MAPS_ID

  const { isLoaded } = useJsApiLoader({
    id: googleMapsId,
    googleMapsApiKey: googleMapsApiKey,
  })

  const explore = useMemo(
    () => (isLoaded && multiple ? exploreOptions('hybrid') : OPTIONS),
    [isLoaded, multiple],
  )

  const formattedCoordinates = multiple ? [] : filterLatLong(data)

  useEffect(() => {
    if (map) addGeeLayers(map, dataMap || [])
  }, [dataMap, map])

  useEffect(() => {
    if (!map || !heatmap?.points.length) return
    const overlay = createHeatmap(map, heatmap.points, heatmap.colors)
    return () => overlay.setMap(null)
  }, [map, heatmap])

  const onLoad = React.useCallback(
    function callback(mapInstance) {
      mapRef.current = mapInstance
      setMap(mapInstance)

      if (formattedCoordinates?.length > 1) {
        const bounds = new window.google.maps.LatLngBounds(center)
        formattedCoordinates.forEach((coord) => bounds.extend(coord))
        mapInstance.fitBounds(bounds)
      } else if (center) {
        mapInstance.setCenter(center)
        mapInstance.setZoom(16)
      }

      if (dataMap?.length) {
        addGeeLayers(mapInstance, dataMap)
      }
    },
    [formattedCoordinates, center, dataMap],
  )

  const addGeeLayers = (mapInstance, layers) => {
    if (!mapInstance) return

    mapInstance.overlayMapTypes.clear()

    layers?.forEach((layer) => {
      if (layer.show) {
        const geeLayer = new window.google.maps.ImageMapType({
          getTileUrl: function (coord, zoom) {
            let url = `https://earthengine.googleapis.com/v1/${layer.mapid}/tiles/${zoom}/${coord.x}/${coord.y}`
            if (layer.token && layer.token.length > 0) {
              url += `?token=${layer.token}`
            }
            return url
          },
          tileSize: new window.google.maps.Size(256, 256),
          name: layer.layerName,
          maxZoom: 20,
        })

        mapInstance.overlayMapTypes.push(geeLayer)
      }
    })
  }

  const onUnmount = React.useCallback(() => {
    setMap(null)
  }, [])

  const selectedKey = selectedCodes.join(',')
  useEffect(() => {
    if (!map || !multiple || !data?.length) return
    const selected = data.filter((el) => selectedCodes.includes(el?.code))
    const allKey = `${data.length}:${data[0]?.code}`
    const pad =
      typeof fitPadding === 'number'
        ? {
            top: fitPadding,
            right: fitPadding,
            bottom: fitPadding,
            left: fitPadding,
          }
        : fitPadding
    let target = null
    if (selected.length) {
      if (fittedRef.current !== selectedKey) target = selected
      fittedRef.current = selectedKey
    } else {
      if (allKeyRef.current !== allKey) target = data
      fittedRef.current = ''
    }
    const firstFit = !allKeyRef.current
    allKeyRef.current = allKey
    if (!target) return
    const bounds = getItemBounds(target)
    if (bounds.isEmpty()) return
    if (firstFit) map.fitBounds(bounds, pad)
    else if (map.getRenderingType?.() === 'VECTOR')
      animateTo(map, bounds, pad, flightRef)
    else flyTo(map, bounds, pad, flightRef)
  }, [map, multiple, data, selectedKey, fitPadding])

  useEffect(() => {
    if (!map) return
    const l = map.addListener('dragstart', () => flightRef.current++)
    return () => l.remove()
  }, [map])

  const symbol = (path, fillColor, scale = 1) => ({
    path,
    scale,
    fillColor,
    fillOpacity: 1,
    strokeColor: '#FFFFFF',
    strokeWeight: 2,
  })
  const dotIcon = (color, isSelected) =>
    symbol(window.google.maps.SymbolPath.CIRCLE, color, isSelected ? 10 : 7)
  const shownOverlay =
    activeOverlay && overlays.find((o) => o.id === activeOverlay.id)

  useEffect(() => {
    if (map && center && !multiple) {
      if (formattedCoordinates?.length > 1) {
        const bounds = new window.google.maps.LatLngBounds(center)
        formattedCoordinates.forEach((coord) => bounds.extend(coord))
        map.fitBounds(bounds)
      } else if (center) {
        map.setCenter(center)
        map.setZoom(16)
      }
    }
  }, [map, center, formattedCoordinates, multiple, data])

  const renderLabel = (el, position, isSelected) =>
    isSelected || (layers.labels && showLabels) ? (
      <OverlayViewF
        key={`label-${el?.code}`}
        position={position}
        mapPaneName="overlayMouseTarget"
        getPixelPositionOffset={(w, h) => ({
          x: isSelected ? 14 : 11,
          y: -h / 2,
        })}
      >
        <div
          className={`gm-pin-label ${isSelected ? 'gm-pin-label--selected' : ''}`}
          onClick={() => onSelectLand(el)}
        >
          {el?.name || el?.landName}
        </div>
      </OverlayViewF>
    ) : null

  return isLoaded ? (
    <div style={{ position: 'relative', width: '100%', height: '100%' }}>
      <GoogleMap
        options={explore}
        mapContainerStyle={containerStyle}
        mapTypeId={
          multiple
            ? mapType === 'satellite'
              ? 'hybrid'
              : 'roadmap'
            : 'satellite'
        }
        zoom={16}
        center={center}
        onLoad={onLoad}
        onUnmount={onUnmount}
        onZoomChanged={() => {
          if (!map) return
          setShowLabels(map.getZoom() >= 14)
          onZoomLevel?.(Math.floor(map.getZoom()))
        }}
        data-testid="map-container"
      >
        {multiple ? (
          <>
            {data?.map((el, i) => {
              const isSelected = selectedCodes.includes(el?.code)
              if (!layers.parcels && !isSelected) return null
              const path = filterLatLong(el?.plottingPoints || [])
              return path.length > 2 ? (
                <MapPolygon
                  key={`poly-${el?.code ?? i}-${isSelected ? 1 : 0}`}
                  path={path}
                  options={{
                    strokeColor: isSelected ? SELECTED_COLOR : NORMAL_FILL,
                    fillColor: isSelected ? SELECTED_COLOR : NORMAL_FILL,
                    fillOpacity: isSelected ? 0.3 : 0.2,
                    strokeWeight: isSelected ? 3 : 2,
                    zIndex: isSelected ? 2 : 1,
                    clickable: true,
                  }}
                  onClick={() => onSelectLand(el)}
                />
              ) : null
            })}
            {data?.map((el, i) => {
              const isSelected = selectedCodes.includes(el?.code)
              if (!layers.points && !isSelected) return null
              const position = getLatLng(el)
              if (!position) return null
              return (
                <React.Fragment
                  key={`m-${el?.code ?? i}-${isSelected ? 1 : 0}`}
                >
                  <MapMarker
                    position={position}
                    title={el?.name || el?.landName}
                    zIndex={isSelected ? 999 : undefined}
                    icon={dotIcon(
                      isSelected
                        ? SELECTED_COLOR
                        : colorFor?.(el) || NORMAL_COLOR,
                      isSelected,
                    )}
                    onClick={() => onSelectLand(el)}
                  />
                  {renderLabel(el, position, isSelected)}
                </React.Fragment>
              )
            })}
            {overlays.map((o) => (
              <MapMarker
                key={o.id}
                position={o.position}
                title={o.title}
                icon={symbol(SHAPE_PATHS[o.shape], o.color)}
                onClick={() => setActiveOverlay(o)}
              />
            ))}
            {shownOverlay ? (
              <InfoWindowF
                position={shownOverlay.position}
                onCloseClick={() => setActiveOverlay(null)}
              >
                <div className="gm-info">{shownOverlay.info}</div>
              </InfoWindowF>
            ) : null}
          </>
        ) : (
          <>
            {formattedCoordinates?.length > 2 && (
              <MapPolygon
                onClick={onShowDetailLand}
                path={formattedCoordinates}
                options={{
                  strokeColor: '#006386',
                  fillColor: '#7ECBE5',
                  strokeWeight: 5,
                }}
              />
            )}
            <MapMarker
              onClick={onShowDetailLand}
              position={{
                lat:
                  data?.[0]?.plottingPoint?.coordinates?.[1] ??
                  data?.[0]?.locationPoint?.coordinates?.[1] ??
                  center?.lat,
                lng:
                  data?.[0]?.plottingPoint?.coordinates?.[0] ??
                  data?.[0]?.locationPoint?.coordinates?.[0] ??
                  center?.lng,
              }}
            />
          </>
        )}
      </GoogleMap>
      {multiple ? (
        <>
          <div className="gm-layers" role="group" aria-label="Jenis peta">
            {[
              ['roadmap', 'Map'],
              ['satellite', 'Satelit'],
            ].map(([type, label]) => (
              <button
                key={type}
                type="button"
                className={`gm-chip ${mapType === type ? 'gm-chip--active' : ''}`}
                aria-pressed={mapType === type}
                onClick={() => setMapType(type)}
              >
                {label}
              </button>
            ))}
            {controls}
          </div>
        </>
      ) : null}
    </div>
  ) : null
}

export default Map

    

    